// @flow
import React, { PureComponent, Fragment, type ElementRef, type Node } from 'react';
import { isEmpty } from 'lodash';
import classnames from 'classnames';
import { FocusOn } from 'react-focus-on';

import { ICONS } from 'Components/utils/icon/icons';
import { COLORS } from 'Components/utils/color/colors';
import { stickyNav } from 'Components/widgets/navigation/NavigationSticky.jsx';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';
import { type LanguagesProps } from 'Components/widgets/languages/Languages.jsx';
import {
  elements as ELEMENTS,
  heights as HEIGHTS,
  NAVIGATION_OFFSET_SELECTOR,
  ROOT_ID,
} from 'Components/widgets/navigation/Navigation.constants';
import {
  NAVIGATION_TRACKING_LOGO_BUTTON,
  NAVIGATION_TRACKING_CONTACT_BUTTON,
} from 'Components/widgets/navigation/Navigation.tracking';
import { NavigationContact } from 'Components/widgets/navigation/contact/NavigationContact.jsx';
import { BodyLocker } from 'Components/frameworks/body-locker/BodyLocker.jsx';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { GlobalSearch } from 'Components/widgets/global-search/GlobalSearch.jsx';
import { NavigationMemberAccessButton } from 'Components/widgets/navigation/member-access/member-access-button/NavigationMemberAccessButton.jsx';
import { NavigationMemberAccess } from 'Components/widgets/navigation/member-access/NavigationMemberAccess.jsx';

import { SideNavigation, type NavItem } from './SideNavigation.jsx';
import { SubItems } from './SubItems.jsx';
import { NavigationNarrowElementPanel } from './NavigationNarrowElementPanel.jsx';

type Props = {
  backButtonLabel?: string,
  backButtonMainNavLabel?: string,
  backToB2CWebsiteLabel?: string,
  backToB2CWebsiteLink?: string,
  closeMainNavLabel?: string,
  cmsAccount: CMSAccount,
  cmsMemberSpace: CMSMemberSpace,
  contact?: CMSNavigationBarContact,
  id?: string,
  isMicePage: boolean,
  isSticky: boolean,
  items: Array<NavItem>,
  language?: LanguagesProps,
  MICELogoRedirectionLink: string,
  onLock: boolean => void,
  openMainNavLabel?: string,
  promoBannerRef: { current: ElementRef<Node> | null },
  userInfo?: UserInfo,
};

type State = {
  activeElement: string | null,
  sideNavigationPanels: Array<string>,
};

export class NavigationNarrow extends PureComponent<Props, State> {
  static defaultProps = {
    backButtonLabel: '',
    contact: {},
    id: 'Navigation',
    items: [],
    onLock: () => {},
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      activeElement: null,
      sideNavigationPanels: [],
    };
  }

  toggleElement = (activeElement: ?string) => () => {
    this.setState(
      prevState => ({
        activeElement: prevState.activeElement === activeElement ? null : activeElement,
        sideNavigationPanels: [],
      }),
      () => this.props.onLock(!!this.state.activeElement),
    );
  };

  resetNav = (): void => {
    this.setState(
      {
        activeElement: null,
        sideNavigationPanels: [],
      },
      () => this.props.onLock(false),
    );
  };

  navBack = (): void => {
    const { sideNavigationPanels } = this.state;

    this.setState({
      sideNavigationPanels: sideNavigationPanels.splice(0, sideNavigationPanels.length - 1),
    });
  };

  navForward = (nextPanel: ?string): void => {
    const { sideNavigationPanels } = this.state;

    nextPanel && !sideNavigationPanels.includes(nextPanel)
      ? this.setState({
          sideNavigationPanels: [...sideNavigationPanels, nextPanel],
        })
      : this.toggleElement(ELEMENTS.MENU)();
  };

  closeMenu = (): void => {
    this.toggleElement(ELEMENTS.MENU)();
  };

  render() {
    const {
      backButtonLabel,
      backButtonMainNavLabel,
      backToB2CWebsiteLabel,
      backToB2CWebsiteLink,
      closeMainNavLabel,
      cmsAccount,
      cmsMemberSpace,
      contact,
      id,
      isMicePage,
      isSticky,
      items,
      language,
      openMainNavLabel,
      promoBannerRef,
      userInfo,
      MICELogoRedirectionLink,
    } = this.props;
    const { activeElement, sideNavigationPanels } = this.state;

    const offsetTop =
      !isSticky && promoBannerRef && promoBannerRef.current
        ? promoBannerRef.current.offsetHeight
        : 0;

    const panelStyles = {
      maxWidth: '31rem',
      top: HEIGHTS.NAVIGATION_NARROW_HEIGHT + offsetTop,
      transition: 'transform linear 330ms',
    };

    const hasContactDetails =
      contact?.phoneNumber ||
      contact?.hours ||
      contact?.description ||
      contact?.toll ||
      contact?.title;

    return (
      <nav className={classnames('bg-white w-full', NAVIGATION_OFFSET_SELECTOR)} id={id}>
        <FocusOn allowPinchZoom enabled={!!activeElement}>
          <BodyLocker isLocked={!!activeElement}>
            <div
              className="border-b-1 flex flex-no-wrap"
              style={{ height: HEIGHTS.NAVIGATION_NARROW_HEIGHT }}
            >
              <div className="rtl:border-r-1 border-gray-light flex items-center h-full">
                <button
                  id="NavigationNarrow-toggleSideNav-button"
                  className="reset-button h-full p-4 text-blue"
                  onClick={this.toggleElement(ELEMENTS.MENU)}
                  type="button"
                >
                  <span className="a11y">
                    {activeElement === ELEMENTS.MENU ? closeMainNavLabel : openMainNavLabel}
                  </span>
                  <div className="hamburger">
                    <span
                      className={classnames('hamburger-span', {
                        'hamburger-span-active': activeElement === ELEMENTS.MENU,
                      })}
                    />
                  </div>
                </button>
              </div>
              <SideNavigation
                id={ROOT_ID}
                isActive={activeElement === ELEMENTS.MENU && !sideNavigationPanels.length}
                isHidden={activeElement !== ELEMENTS.MENU}
                items={items}
                language={language}
                navBack={this.navBack}
                navForward={this.navForward}
                closeMenu={this.closeMenu}
                style={panelStyles}
                isMicePage={isMicePage}
                backToB2CWebsiteLink={backToB2CWebsiteLink}
                backToB2CWebsiteLabel={backToB2CWebsiteLabel}
              />
              <SubItems
                parentId={ROOT_ID}
                items={items}
                sideNavigationPanels={sideNavigationPanels}
                backButtonLabel={backButtonLabel}
                backButtonMainNavLabel={backButtonMainNavLabel}
                language={language}
                panelStyles={panelStyles}
                navBack={this.navBack}
                navForward={this.navForward}
                closeMenu={this.closeMenu}
              />
              <div className="flex-1 px-2">
                <TrackClick
                  as={DynamicLink}
                  className="flex items-center"
                  data-testid="NavigationNarrow-logo-link"
                  onClick={this.resetNav}
                  style={{ height: HEIGHTS.NAVIGATION_NARROW_HEIGHT }}
                  trackingData={NAVIGATION_TRACKING_LOGO_BUTTON}
                  url={`${isMicePage ? MICELogoRedirectionLink : '/'}`}
                >
                  <Icon
                    className={classnames({ 'mt-1': isMicePage })}
                    color={COLORS.BLUE}
                    isNotSquare
                    name={isMicePage ? ICONS.CLUBMED_MEETINGS : ICONS.CLUBMED}
                    width={isMicePage ? '9rem' : '6rem'}
                  />
                  <span className="a11y">
                    {isMicePage ? 'Club Med meetings and events page' : 'Club Med Homepage'}
                  </span>
                </TrackClick>
              </div>
              <div className="flex">
                {!isMicePage && (
                  <GlobalSearch
                    className={classnames(
                      'reset-button flex items-center px-2 relative navigation-arrow',
                      {
                        'text-gray-darker': activeElement !== ELEMENTS.SEARCH,
                        'text-blue navigation-arrow-active': activeElement === ELEMENTS.SEARCH,
                      },
                    )}
                    isOpen={activeElement === ELEMENTS.SEARCH}
                    offsetTop={offsetTop}
                    toggleOpen={this.toggleElement(ELEMENTS.SEARCH)}
                  />
                )}

                {!isEmpty(contact) && (
                  <Fragment>
                    <TrackClick
                      as="button"
                      aria-expanded={activeElement === ELEMENTS.CONTACT}
                      className={classnames(
                        'reset-button flex items-center px-2 relative navigation-arrow',
                        {
                          'text-gray-darker': activeElement !== ELEMENTS.CONTACT,
                          'text-blue navigation-arrow-active': activeElement === ELEMENTS.CONTACT,
                        },
                      )}
                      data-testid="NavigationNarrow-contact-button"
                      onClick={hasContactDetails ? this.toggleElement(ELEMENTS.CONTACT) : undefined}
                      title={contact.title}
                      trackingData={
                        activeElement === ELEMENTS.CONTACT || !hasContactDetails
                          ? null
                          : NAVIGATION_TRACKING_CONTACT_BUTTON
                      }
                      type="button"
                    >
                      <Icon name={ICONS.PHONE} width="1.1875rem" />
                      <span className="a11y">{contact.title}</span>
                    </TrackClick>
                    <NavigationNarrowElementPanel
                      isActive={activeElement === ELEMENTS.CONTACT}
                      style={panelStyles}
                    >
                      {activeElement === ELEMENTS.CONTACT && (
                        <NavigationContact contact={contact} hasTel />
                      )}
                    </NavigationNarrowElementPanel>
                  </Fragment>
                )}

                {!isMicePage && (
                  <Fragment>
                    <NavigationMemberAccessButton
                      className={classnames(
                        'reset-button flex items-center px-2 relative navigation-arrow',
                        {
                          'text-gray-darker': activeElement !== ELEMENTS.LOGIN,
                          'text-blue navigation-arrow-active': activeElement === ELEMENTS.LOGIN,
                        },
                      )}
                      isActive={activeElement === ELEMENTS.LOGIN}
                      isAuthenticated={userInfo && userInfo.isLoggedIn}
                      isQuickLoggedIn={userInfo && userInfo.isQuickLoggedIn}
                      onClick={this.toggleElement(ELEMENTS.LOGIN)}
                      title={this.props.cmsMemberSpace?.title}
                    />
                    <NavigationNarrowElementPanel
                      className={classnames(
                        'w-9/10 bg-white fixed bottom-0 rtl:right-0 p-5 rounded-small overflow-y-auto',
                        {
                          'shadow-xs': activeElement === ELEMENTS.LOGIN,
                        },
                      )}
                      isActive={activeElement === ELEMENTS.LOGIN}
                      style={panelStyles}
                    >
                      {activeElement === ELEMENTS.LOGIN && (
                        <NavigationMemberAccess
                          cmsAccount={cmsAccount}
                          cmsMemberSpace={cmsMemberSpace}
                          userInfo={userInfo}
                        />
                      )}
                    </NavigationNarrowElementPanel>
                  </Fragment>
                )}
              </div>
            </div>
          </BodyLocker>
        </FocusOn>
      </nav>
    );
  }
}

export const StickyNavigationNarrow = stickyNav(NavigationNarrow);
